// Pure auto-grouping logic. Returns derived "groups" from a flat POI list:
//   - by-city:      one group per trip city or unassigned-locality with ≥1 POI
//   - by-category:  one group per Google primary type with ≥2 POIs

import { formatTypeLabel } from './place-type.js';
import type { GroupTint, PoiView, TripCityView } from './api.js';

interface AutoGroupBase {
  /** Stable string id used as the filter key. Avoids id collisions with
   *  user groups (which are numeric). The string format is opaque to
   *  consumers — branch on `kind` instead of parsing the key. */
  key: string;
  name: string;
  tint: GroupTint;
  members: PoiView[];
}

/**
 * Discriminated union so consumers don't reverse-engineer info from the key
 * string. `tripCity` carries the cityId for collapsing into activeCityId;
 * `locality` is identifiable by `name` alone (no relational id exists yet).
 */
export type AutoGroup =
  | (AutoGroupBase & { kind: 'tripCity'; cityId: number })
  | (AutoGroupBase & { kind: 'locality' })
  | (AutoGroupBase & { kind: 'category' });

const CITY_TINT: GroupTint = 'sky';
const CATEGORY_TINT: GroupTint = 'mint';

/**
 * Auto-groups by city, drawn from two sources:
 *
 *   1. POIs with a `cityId` joined against trip_cities — one group per
 *      trip city with at least one assigned POI (kind: 'tripCity').
 *   2. POIs with `cityId === null` but a non-null `locality` — one group
 *      per locality string, e.g. "Nagoya" when the user bookmarks places
 *      there without adding Nagoya as a trip city (kind: 'locality').
 */
export function groupByCity(pois: PoiView[], cities: TripCityView[]): AutoGroup[] {
  const out: AutoGroup[] = [];

  const byId = new Map<number, PoiView[]>();
  for (const p of pois) {
    if (p.cityId === null) continue;
    if (!byId.has(p.cityId)) byId.set(p.cityId, []);
    byId.get(p.cityId)!.push(p);
  }
  for (const c of cities) {
    const members = byId.get(c.id);
    if (!members || members.length === 0) continue;
    out.push({
      kind: 'tripCity',
      key: `auto:tripcity:${c.id}`,
      name: c.name,
      tint: CITY_TINT,
      cityId: c.id,
      members,
    });
  }

  const byLocality = new Map<string, PoiView[]>();
  for (const p of pois) {
    if (p.cityId !== null) continue;
    if (!p.locality) continue;
    if (!byLocality.has(p.locality)) byLocality.set(p.locality, []);
    byLocality.get(p.locality)!.push(p);
  }
  for (const [locality, members] of byLocality) {
    out.push({
      kind: 'locality',
      key: `auto:locality:${locality}`,
      name: locality,
      tint: CITY_TINT,
      members,
    });
  }

  return out;
}

/**
 * One auto-group per `pois.category` value with at least `minPois` members
 * (default 2 — singletons aren't useful as a "group"). Sorted by member
 * count desc.
 */
export function groupByCategory(pois: PoiView[], minPois = 2): AutoGroup[] {
  const byCat = new Map<string, PoiView[]>();
  for (const p of pois) {
    if (!p.category) continue;
    if (!byCat.has(p.category)) byCat.set(p.category, []);
    byCat.get(p.category)!.push(p);
  }
  const out: AutoGroup[] = [];
  for (const [cat, members] of byCat) {
    if (members.length < minPois) continue;
    out.push({
      kind: 'category',
      key: `auto:cat:${cat}`,
      name: formatTypeLabel(cat) ?? cat,
      tint: CATEGORY_TINT,
      members,
    });
  }
  return out.sort((a, b) => b.members.length - a.members.length);
}
